/**
 * Loads the Google Maps JavaScript API once, with the browser key fetched at
 * runtime from the server (GOOGLE_MAPS_BROWSER_KEY). Nothing is baked into the
 * bundle, so the key can rotate without a rebuild; the key itself is
 * referrer-restricted in Google Cloud.
 */
import { settingsService } from '../services/api';

let pending = null;

export class MapsLoadError extends Error {}

// Google calls this global when the key is rejected (bad key, referrer not on
// the allow-list, API not enabled). The map div goes grey — we surface why.
if (typeof window !== 'undefined' && !window.gm_authFailure) {
  window.gm_authFailure = () => {
    window.__gidMapsAuthFailed = true;
    window.dispatchEvent(new Event('gid-maps-auth-failed'));
  };
}

/**
 * The estimator magnifies past Google's maximum imagery zoom by CSS-scaling the
 * map div (layout W/S × H/S, transform scale(S)). Google sizes its viewport
 * from getBoundingClientRect(), which includes that transform — so it kept
 * believing the map was full-size, painted a full-size viewport into the
 * smaller box, and every projection was off by the difference (Bug #387: a
 * 6 px drag jumped 15 m and ran away). This shim divides the scale back out
 * for elements inside a `[data-est-mag]` map div, so Google's pixel space is
 * the div's own layout box. Installed once, before the Maps script loads, and
 * a no-op everywhere else (and whenever the magnification is 1).
 */
let shimInstalled = false;
function installMagnificationShim() {
  if (shimInstalled || typeof Element === 'undefined') return;
  shimInstalled = true;
  const original = Element.prototype.getBoundingClientRect;
  Element.prototype.getBoundingClientRect = function getBoundingClientRectUnscaled() {
    const r = original.call(this);
    const host = this.closest ? this.closest('[data-est-mag]') : null;
    const S = host ? Number(host.dataset.estMag) || 1 : 1;
    if (S === 1) return r;
    const width = r.width / S, height = r.height / S;
    return {
      x: r.x, y: r.y, left: r.left, top: r.top, width, height, right: r.left + width, bottom: r.top + height,
      toJSON() { return { x: r.x, y: r.y, width, height, top: r.top, left: r.left, right: r.left + width, bottom: r.top + height }; }
    };
  };
}

export function loadGoogleMaps() {
  if (window.google?.maps?.geometry) return Promise.resolve(window.google.maps);
  if (pending) return pending;
  installMagnificationShim();
  pending = (async () => {
    const r = await settingsService.mapsConfig();
    if (!r.success) throw new MapsLoadError(r.error || 'Could not fetch the map configuration');
    if (!r.key) throw new MapsLoadError('No Google Maps key on this server — add GOOGLE_MAPS_BROWSER_KEY to the environment and restart.');
    await new Promise((resolve, reject) => {
      const cb = '__gidMapsReady';
      window[cb] = () => { delete window[cb]; resolve(); };
      const s = document.createElement('script');
      s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(r.key)}&libraries=geometry&v=weekly&loading=async&callback=${cb}`;
      s.async = true;
      s.onerror = () => reject(new MapsLoadError('Google Maps failed to load — check the internet connection'));
      document.head.appendChild(s);
    });
    if (!window.google?.maps?.geometry) throw new MapsLoadError('Google Maps loaded without the geometry library');
    return window.google.maps;
  })();
  pending.catch(() => { pending = null; });
  return pending;
}
